import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  SharepointDocumentLibrary,
  IntFolder,
} from '@gen2/api/integrations/api';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { ModalCloseButton } from '@nx-fe/components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSharepointDocumentLibrariesQuery } from '../../../../api/integrations/hooks';
import { useIntegrationsStore } from '../../store';
import { StyledDialog } from '../sharepoint-sites-modal/sharepoint-sites-modal.styled';

const SharepointDocumentLibrariesModal = () => {
  const { t } = useTranslation('integrations');
  const {
    documentLibrary,
    cancelDocumentLibrary,
    confirmDocumentLibrary,
    site,
    setFinderOpen,
    setCurrentFolder,
    selectedFolder,
  } = useIntegrationsStore();
  const { data } = useSharepointDocumentLibrariesQuery({
    enabled: documentLibrary.isOpen && !!site.id,
    siteId: site.id,
  });
  const [currentLibrary, setCurrentLibrary] =
    useState<SharepointDocumentLibrary>({
      id: '',
      name: '',
      path: '',
    });

  useEffect(() => {
    if (!documentLibrary.id) return;

    setCurrentLibrary({
      id: documentLibrary.id,
      name: documentLibrary.name,
      path: documentLibrary.path || '',
    });
  }, [documentLibrary, setCurrentLibrary]);

  // pre-select and auto-confirm if there is only one library (only on initial setup, not on reload)
  useEffect(() => {
    if (!data?.data?.document_libraries?.length) return;
    // Don't auto-confirm if a folder is already selected (page reload scenario)
    if (selectedFolder?.id) return;

    if (data.data.document_libraries.length === 1) {
      const singleLibrary = data.data.document_libraries[0];
      setCurrentLibrary(singleLibrary);

      // Auto-confirm single library selection
      confirmDocumentLibrary({
        id: singleLibrary.id,
        name: singleLibrary.name,
        path: singleLibrary.path,
        isOpen: false,
      });

      // Reset current folder and show folder selection
      setCurrentFolder({
        id: '',
        name: '',
        path: '',
      });
      setFinderOpen(true);
    }
  }, [
    data,
    confirmDocumentLibrary,
    setCurrentFolder,
    setFinderOpen,
    selectedFolder?.id,
  ]);

  const handleLibraryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const pickedLibrary = data?.data?.document_libraries.find(
      (l) => l.id === event.target.value,
    );

    if (!pickedLibrary) return;
    setCurrentLibrary(pickedLibrary);
  };

  const onContinue = () => {
    if (!currentLibrary.id) return;

    confirmDocumentLibrary({
      id: currentLibrary.id,
      name: currentLibrary.name,
      path: currentLibrary.path,
      isOpen: false,
    });

    // Reset current folder and show folder selection
    setCurrentFolder({
      id: '',
      name: '',
      path: '',
    });
    setFinderOpen(true);
  };

  const handleCloseFinder = () => {
    setFinderOpen(false);
  };

  const handleSelectFolder = (folder: IntFolder) => {
    setCurrentFolder(folder);
    setFinderOpen(false);
  };

  return (
    <StyledDialog
      open={Boolean(site?.id) && documentLibrary.isOpen}
      onClose={cancelDocumentLibrary}
      aria-labelledby="document-libraries-modal"
      aria-describedby="document-libraries-modal"
      data-cy="document-libraries-modal"
      style={{ zIndex: 9999 }}
    >
      <ModalCloseButton
        data-cy="document-libraries-modal-close"
        aria-label="close"
        onClick={cancelDocumentLibrary}
      >
        <FontAwesomeIcon icon={faXmark} />
      </ModalCloseButton>
      <DialogTitle id="alert-dialog-title">
        {t('sharepoint.document_libraries.title')}
      </DialogTitle>
      <DialogContent dividers>
        <RadioGroup
          aria-labelledby="document-libraries-label"
          name="library"
          onChange={handleLibraryChange}
          value={currentLibrary.id}
          defaultValue={documentLibrary.id}
        >
          {!!data?.data?.document_libraries?.length &&
            data.data.document_libraries.map((l) => (
              <FormControlLabel
                key={l.id}
                value={l.id}
                control={<Radio color="secondary" />}
                label={l.name}
              />
            ))}
        </RadioGroup>
      </DialogContent>
      <DialogActions>
        <Button
          data-cy="integration-confirm-cancel"
          onClick={cancelDocumentLibrary}
          color="tertiary"
          variant="outlined"
        >
          {t('sharepoint.document_libraries.cancel_btn')}
        </Button>
        <Button
          data-cy="integration-confirm-save"
          onClick={onContinue}
          color="primary"
          variant="contained"
        >
          {t('sharepoint.document_libraries.confirm_btn')}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default SharepointDocumentLibrariesModal;
